import { LeadLandingFormConfig, LeadLandingFormField, LeadLandingSuccessConfig } from './lead-link.model';

export type LandingTemplateV2Key = 'consultation' | 'offer' | 'event' | 'lead-magnet';

export type LandingBlockV2Type = 'hero' | 'benefits' | 'content' | 'proof' | 'process' | 'agenda' | 'faq' | 'form';

export interface LandingThemeV2 {
    brandName: string;
    logoUrl?: string;
    websiteUrl?: string;
    primaryColor: string;
    accentColor: string;
    backgroundColor: string;
    textColor: string;
    fontFamily: string;
    tone: string;
    source: 'tenant' | 'suggested' | 'manual';
    configuredAt?: string;
}

export interface LandingBlockItemV2 {
    id: string;
    title: string;
    body?: string;
}

export interface LandingBlockV2 {
    id: string;
    type: LandingBlockV2Type;
    label: string;
    visible: boolean;
    eyebrow?: string;
    title: string;
    body?: string;
    ctaLabel?: string;
    imageUrl?: string;
    quote?: string;
    quoteAuthor?: string;
    items?: LandingBlockItemV2[];
    variant?: string;
}

export interface LandingAdvancedOverrideV2 {
    enabled: boolean;
    html: string;
    css: string;
}

export interface LandingProjectV2 {
    version: 2;
    templateKey: LandingTemplateV2Key;
    name: string;
    slug: string;
    theme: LandingThemeV2;
    blocks: LandingBlockV2[];
    form: LeadLandingFormConfig;
    success: Required<Pick<LeadLandingSuccessConfig, 'title' | 'message'>>;
    warnings: string[];
    advancedOverride?: LandingAdvancedOverrideV2;
    updatedAt: string;
}

export interface LandingTemplateV2 {
    key: LandingTemplateV2Key;
    label: string;
    description: string;
    bestFor: string;
    defaultName: string;
    blocks: LandingBlockV2[];
    form: LeadLandingFormConfig;
    success: Required<Pick<LeadLandingSuccessConfig, 'title' | 'message'>>;
}

const DEFAULT_FORM_FIELDS: LeadLandingFormField[] = [
    { key: 'firstName', label: 'Nome', type: 'text', required: true },
    { key: 'lastName', label: 'Cognome', type: 'text', required: true },
    { key: 'phone', label: 'Telefono', type: 'tel', required: true },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'message', label: 'Messaggio', type: 'textarea' }
];

export const DEFAULT_LANDING_THEME_V2: LandingThemeV2 = {
    brandName: 'Il tuo brand',
    primaryColor: '#3157e6',
    accentColor: '#16b981',
    backgroundColor: '#f7f9fc',
    textColor: '#0f172a',
    fontFamily: 'Inter, system-ui, sans-serif',
    tone: 'Professionale e concreto',
    source: 'suggested'
};

export const LANDING_TEMPLATES_V2: LandingTemplateV2[] = [
    {
        key: 'consultation',
        label: 'Consulenza',
        description: 'Per raccogliere richieste di ricontatto o valutazione personalizzata.',
        bestFor: 'Servizi consulenziali, valutazioni, primo contatto qualificato.',
        defaultName: 'Consulenza',
        blocks: [
            {
                id: 'hero',
                type: 'hero',
                label: 'Hero',
                visible: true,
                eyebrow: 'Valutazione gratuita',
                title: 'Parla con un consulente e chiarisci i prossimi passi',
                body: 'Lascia i tuoi dati: ti ricontatteremo per capire la richiesta e darti indicazioni concrete.',
                ctaLabel: 'Richiedi una consulenza',
                variant: 'split'
            },
            {
                id: 'benefits',
                type: 'benefits',
                label: 'Benefici',
                visible: true,
                title: 'Un primo confronto semplice, senza passaggi inutili',
                items: [
                    {
                        id: 'benefit-1',
                        title: 'Richiesta senza impegno',
                        body: 'Raccogliamo le informazioni essenziali e capiamo se possiamo aiutarti.'
                    },
                    {
                        id: 'benefit-2',
                        title: 'Risposta dal team dedicato',
                        body: 'La richiesta arriva al percorso corretto e viene gestita dal referente indicato.'
                    },
                    {
                        id: 'benefit-3',
                        title: 'Prossimi passi chiari',
                        body: 'Ricevi indicazioni pratiche su cosa preparare e come procedere.'
                    }
                ]
            },
            {
                id: 'process',
                type: 'process',
                label: 'Cosa succede dopo',
                visible: true,
                title: 'Cosa succede dopo il form',
                items: [
                    { id: 'step-1', title: 'Invii la richiesta' },
                    { id: 'step-2', title: 'Il team valuta il contesto' },
                    { id: 'step-3', title: 'Ricevi un ricontatto dedicato' }
                ]
            },
            {
                id: 'faq',
                type: 'faq',
                label: 'FAQ',
                visible: true,
                title: 'Domande frequenti',
                items: [
                    {
                        id: 'faq-1',
                        title: 'La richiesta è vincolante?',
                        body: 'No, serve solo ad aprire un primo confronto.'
                    },
                    {
                        id: 'faq-2',
                        title: 'Devo preparare documenti?',
                        body: 'No, bastano i dati richiesti. Eventuali dettagli arriveranno nel ricontatto.'
                    }
                ]
            },
            {
                id: 'form',
                type: 'form',
                label: 'Form',
                visible: true,
                title: 'Ti ricontattiamo noi',
                body: 'Compila i dati essenziali: useremo queste informazioni solo per gestire la richiesta.'
            }
        ],
        form: {
            title: 'Ti ricontattiamo noi',
            submitLabel: 'Richiedi una consulenza',
            fields: DEFAULT_FORM_FIELDS
        },
        success: {
            title: 'Richiesta ricevuta',
            message: 'Grazie, abbiamo ricevuto i tuoi dati. Ti ricontatteremo a breve.'
        }
    },
    {
        key: 'offer',
        label: 'Offerta o promo',
        description: 'Per campagne ADV o promozioni con CTA immediata.',
        bestFor: 'Traffico freddo, campagne paid, offerte commerciali semplici.',
        defaultName: 'Promozione',
        blocks: [
            {
                id: 'hero',
                type: 'hero',
                label: 'Hero',
                visible: true,
                eyebrow: 'Offerta dedicata',
                title: 'Richiedi una valutazione e verifica i prossimi passi',
                body: 'Compila il form con i dati essenziali: ti ricontatteremo con indicazioni chiare.',
                ctaLabel: 'Richiedi valutazione',
                variant: 'form-first'
            },
            {
                id: 'benefits',
                type: 'benefits',
                label: 'Perché conviene',
                visible: true,
                title: 'Perché lasciare i dati ora',
                items: [
                    { id: 'benefit-1', title: 'Valutazione senza impegno', body: 'Capisci se la proposta è adatta.' },
                    {
                        id: 'benefit-2',
                        title: 'Riscontro rapido',
                        body: 'Il team può richiamarti con il contesto già chiaro.'
                    },
                    {
                        id: 'benefit-3',
                        title: 'Percorso ordinato',
                        body: 'La richiesta entra subito nel flusso corretto.'
                    }
                ]
            },
            {
                id: 'proof',
                type: 'proof',
                label: 'Rassicurazione',
                visible: true,
                title: 'Una richiesta breve, un ricontatto più utile',
                body: 'Il form serve a evitare passaggi generici e rendere il primo contatto più concreto.',
                quote: 'Preferiamo chiedere pochi dati utili e poi completare la valutazione insieme.',
                quoteAuthor: 'Team consulenza'
            },
            {
                id: 'faq',
                type: 'faq',
                label: 'FAQ',
                visible: true,
                title: 'Prima di inviare',
                items: [
                    {
                        id: 'faq-1',
                        title: 'È una richiesta gratuita?',
                        body: 'Sì, il primo contatto non comporta impegni.'
                    },
                    {
                        id: 'faq-2',
                        title: 'Quando vengo ricontattato?',
                        body: 'Ti ricontatteremo appena il team avrà preso in carico la richiesta.'
                    }
                ]
            },
            {
                id: 'form',
                type: 'form',
                label: 'Form',
                visible: true,
                title: 'Ricevi la tua valutazione',
                body: 'Invia la richiesta e verrai ricontattato per completare i dettagli.'
            }
        ],
        form: {
            title: 'Ricevi la tua valutazione',
            submitLabel: 'Richiedi valutazione',
            fields: DEFAULT_FORM_FIELDS
        },
        success: {
            title: 'Valutazione richiesta',
            message: 'La richiesta è stata registrata. Ti contatteremo per completare la valutazione.'
        }
    },
    {
        key: 'event',
        label: 'Evento o webinar',
        description: 'Per registrazioni a eventi, webinar, open day o liste invito.',
        bestFor: 'Eventi commerciali, webinar informativi, appuntamenti pubblici.',
        defaultName: 'Evento',
        blocks: [
            {
                id: 'hero',
                type: 'hero',
                label: 'Hero',
                visible: true,
                eyebrow: 'Registrazione aperta',
                title: 'Partecipa all’evento e ricevi le informazioni utili prima di iniziare',
                body: 'Lascia i tuoi dati per ricevere conferma, dettagli e prossimi passaggi.',
                ctaLabel: 'Registrati all’evento',
                variant: 'event'
            },
            {
                id: 'agenda',
                type: 'agenda',
                label: 'Programma',
                visible: true,
                title: 'Cosa troverai',
                items: [
                    { id: 'agenda-1', title: 'Introduzione al tema', body: 'Una panoramica semplice per orientarsi.' },
                    { id: 'agenda-2', title: 'Domande pratiche', body: 'Spazio per chiarire dubbi e prossimi passi.' },
                    { id: 'agenda-3', title: 'Follow-up', body: 'Indicazioni per chi vuole approfondire.' }
                ]
            },
            {
                id: 'benefits',
                type: 'benefits',
                label: 'Per chi è',
                visible: true,
                title: 'Pensato per chi vuole capire prima di decidere',
                items: [
                    { id: 'benefit-1', title: 'Registrazione semplice' },
                    { id: 'benefit-2', title: 'Dettagli inviati al contatto indicato' },
                    { id: 'benefit-3', title: 'Ricontatto se vuoi approfondire' }
                ]
            },
            {
                id: 'faq',
                type: 'faq',
                label: 'FAQ',
                visible: true,
                title: 'Informazioni utili',
                items: [
                    {
                        id: 'faq-1',
                        title: 'Riceverò una conferma?',
                        body: 'Sì, useremo i dati indicati per confermare la registrazione.'
                    },
                    {
                        id: 'faq-2',
                        title: 'Posso fare domande?',
                        body: 'Sì, puoi usare il campo messaggio per aggiungere contesto.'
                    }
                ]
            },
            {
                id: 'form',
                type: 'form',
                label: 'Form',
                visible: true,
                title: 'Completa la registrazione',
                body: 'Inserisci i dati essenziali per ricevere conferma e dettagli disponibili.'
            }
        ],
        form: {
            title: 'Completa la registrazione',
            submitLabel: 'Registrati',
            fields: DEFAULT_FORM_FIELDS
        },
        success: {
            title: 'Registrazione ricevuta',
            message: 'Abbiamo salvato la tua richiesta. Riceverai i dettagli al contatto indicato.'
        }
    },
    {
        key: 'lead-magnet',
        label: 'Guida o contenuto',
        description: 'Per offrire una guida, checklist o contenuto scaricabile in cambio del contatto.',
        bestFor: 'Lead magnet, funnel educativi, campagne con contenuto informativo.',
        defaultName: 'Guida gratuita',
        blocks: [
            {
                id: 'hero',
                type: 'hero',
                label: 'Hero',
                visible: true,
                eyebrow: 'Guida gratuita',
                title: 'Ricevi una guida pratica per orientarti meglio',
                body: 'Lascia i tuoi dati e ricevi indicazioni semplici per capire cosa valutare prima del prossimo passo.',
                ctaLabel: 'Ricevi la guida',
                variant: 'guide'
            },
            {
                id: 'content',
                type: 'content',
                label: 'Contenuto',
                visible: true,
                title: 'Cosa contiene',
                body: 'Una raccolta di punti pratici per leggere meglio la tua situazione e preparare un confronto più utile.',
                items: [
                    { id: 'content-1', title: 'Criteri da valutare' },
                    { id: 'content-2', title: 'Domande da fare prima di scegliere' },
                    { id: 'content-3', title: 'Prossimi passi consigliati' }
                ]
            },
            {
                id: 'benefits',
                type: 'benefits',
                label: 'Valore',
                visible: true,
                title: 'Utile se vuoi arrivare preparato',
                items: [
                    { id: 'benefit-1', title: 'Più chiarezza' },
                    { id: 'benefit-2', title: 'Meno passaggi generici' },
                    { id: 'benefit-3', title: 'Un confronto più concreto' }
                ]
            },
            {
                id: 'faq',
                type: 'faq',
                label: 'FAQ',
                visible: true,
                title: 'Prima di richiederla',
                items: [
                    { id: 'faq-1', title: 'La guida è gratuita?', body: 'Sì, serve ad aiutarti a orientarti meglio.' },
                    {
                        id: 'faq-2',
                        title: 'Sarò ricontattato?',
                        body: 'Potremo ricontattarti per darti supporto sui prossimi passi.'
                    }
                ]
            },
            {
                id: 'form',
                type: 'form',
                label: 'Form',
                visible: true,
                title: 'Ricevi il contenuto',
                body: 'Compila il form: useremo i dati per gestire la richiesta e inviarti le informazioni disponibili.'
            }
        ],
        form: {
            title: 'Ricevi il contenuto',
            submitLabel: 'Ricevi la guida',
            fields: DEFAULT_FORM_FIELDS
        },
        success: {
            title: 'Richiesta ricevuta',
            message: 'Abbiamo registrato la tua richiesta. Riceverai le informazioni al contatto indicato.'
        }
    }
];

export function landingTemplateByKey(key: string | null | undefined): LandingTemplateV2 {
    return LANDING_TEMPLATES_V2.find((template) => template.key === key) ?? LANDING_TEMPLATES_V2[0];
}

export function createLandingProjectFromTemplate(
    templateKey: LandingTemplateV2Key,
    theme: LandingThemeV2,
    overrides: Partial<Pick<LandingProjectV2, 'name' | 'slug'>> = {}
): LandingProjectV2 {
    const template = landingTemplateByKey(templateKey);
    const name = overrides.name || template.defaultName;
    return {
        version: 2,
        templateKey,
        name,
        slug: overrides.slug || slugifyLandingProject(name),
        theme: { ...theme },
        blocks: template.blocks.map((block) => ({
            ...block,
            items: block.items?.map((item) => ({ ...item }))
        })),
        form: {
            ...template.form,
            fields: template.form.fields.map((field) => ({ ...field }))
        },
        success: { ...template.success },
        warnings: [],
        advancedOverride: { enabled: false, html: '', css: '' },
        updatedAt: new Date().toISOString()
    };
}

export function isLandingProjectV2(value: unknown): value is LandingProjectV2 {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as Partial<LandingProjectV2>;
    return candidate.version === 2 && typeof candidate.templateKey === 'string' && Array.isArray(candidate.blocks);
}

export function cloneLandingProjectV2(project: LandingProjectV2): LandingProjectV2 {
    return JSON.parse(JSON.stringify(project)) as LandingProjectV2;
}

export function slugifyLandingProject(value: string): string {
    const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
    return slug || 'landing';
}
